import { Turnstile } from '@marsidev/react-turnstile';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useTranslate } from '../../../hooks/useTranslate';
import { useMarket } from '../../../providers/MarketProvider';
import { AlertMessage } from '../../AlertMessage/AlertMessage';
import { useNewsletterSubscription } from '../Newsletter.hooks';
import type { FormFields } from '../Newsletter.types';
import {
  Content,
  Fields,
  StyledButton,
  StyledError,
  StyledForm,
  StyledHeading,
  StyledInput,
  StyledParagraph,
  Wrapper,
} from './NewsletterFooter.styles';

export const NewsletterFooter = () => {
  const { t } = useTranslate();
  const { cfTurnstileSiteKey, lang } = useMarket();
  const [showTurnstile, setShowTurnstile] = useState(false);

  const { isSuccess, isError, isLoading, onSubmit } = useNewsletterSubscription(
    'newsletter-subscription-footer'
  );

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>();

  useEffect(() => {
    register('cfTurnstileResponse', {
      required: t('ui.explore.form.securityRequired'),
    });
  }, [register, t]);

  return (
    <Wrapper>
      <Content>
        <StyledHeading level="h2" mobileSize="xxs" noBottomMargin>
          {t('explore.newsletter.footer.heading')}
        </StyledHeading>
        <StyledParagraph>
          {t('explore.newsletter.footer.paragraph')}
        </StyledParagraph>
        {isSuccess ? (
          <AlertMessage variant="success">
            {t('explore.newsletter.footer.successMessage')}
          </AlertMessage>
        ) : (
          <>
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
              <Fields>
                <StyledInput
                  htmlId="footer-newsletter-subscription-email"
                  type="email"
                  inputMode="email"
                  label=""
                  aria-label={t('newsletter.emailLabel')}
                  error={errors.email?.message}
                  {...register('email', {
                    required: t('newsletter.emailRequired'),
                  })}
                  onFocus={() => setShowTurnstile(true)}
                />

                {showTurnstile && (
                  <div>
                    <Turnstile
                      id="turnstile-newsletter-footer"
                      siteKey={cfTurnstileSiteKey}
                      options={{
                        theme: 'light',
                        language: lang,
                        size: 'flexible',
                      }}
                      onSuccess={(token) =>
                        setValue('cfTurnstileResponse', token, {
                          shouldValidate: true,
                        })
                      }
                    />

                    {errors.cfTurnstileResponse && (
                      <StyledError>
                        {errors.cfTurnstileResponse.message}
                      </StyledError>
                    )}
                  </div>
                )}
              </Fields>

              <StyledButton
                variant="primary"
                size="medium"
                type="submit"
                disabled={isLoading}
              >
                {isLoading
                  ? t('newsletter.subscribingButton')
                  : t('newsletter.subscribeButton')}
              </StyledButton>
            </StyledForm>

            {isError && (
              <StyledError>{t('newsletter.serverError')}</StyledError>
            )}
          </>
        )}
      </Content>
    </Wrapper>
  );
};
